import { Injectable } from '@angular/core';
// Deep paths, NOT the `@features/*` barrels (PLAT-4393 follow-up). Each barrel also
// re-exports its NgModule, and those modules import `@shared` — which re-exports
// `HeaderComponent`, whose own file imports this service's `@core/services` barrel. Going
// through the barrels closed that loop, and under the dev server's unbundled ESM graph the
// cycle left `HeaderComponent` undefined when `setComponentScope` ran, so the whole app
// failed to bootstrap. Importing the services directly keeps the NgModules out of the graph.
import { CommonDialogsService } from '@features/common-dialogs/services/common-dialogs/common-dialogs.service';
import { InfoMessagesService } from '@features/info-messages/services/info-messages.service';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { DraftService } from '../draft/draft.service';

/**
 * The single place that confirms and hard-deletes a `Draft` row (PLAT-4393).
 *
 * **One service, not three duplicated handlers.** All three list pages (Data Source, Connector,
 * Pipeline) need the identical "confirm, then `DELETE /api/drafts/{id}/`, then toast" flow for
 * their draft rows — matching `DraftService`'s own "one service, not four methods" precedent for
 * the same entity-pivot reason: a `Draft` is one shared resource, not three per-entity ones.
 *
 * Deliberately separate from `DraftService` itself, which is documented as a stateless HTTP-only
 * wrapper — this service owns dialog orchestration (`CommonDialogsService` / `InfoMessagesService`),
 * the same split every per-entity `*ActionsService` already uses.
 */
@Injectable({ providedIn: 'root' })
export class DraftActionsService {
  constructor(
    private readonly drafts: DraftService,
    private readonly commonDialogs: CommonDialogsService,
    private readonly infoMessage: InfoMessagesService,
  ) {}

  /**
   * Confirm, then delete a draft. Resolves `true` only when the draft was actually deleted, so
   * callers know whether to refresh their list — `false` for both a cancelled confirmation and a
   * failed delete.
   */
  discardDraft(id: number): Observable<boolean> {
    return this.commonDialogs
      .confirmAction({
        title: 'Delete draft?',
        message: 'Are you sure you want to delete this draft? This action cannot be undone.',
        confirmLabel: 'Delete',
      })
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(false);
          }
          return this.drafts.delete(id).pipe(
            map(() => {
              this.infoMessage.showSuccess('Draft deleted.');
              return true;
            }),
            catchError(() => {
              this.infoMessage.showError('The draft could not be deleted.');
              return of(false);
            }),
          );
        }),
      );
  }
}
